// these are stream types that are not exported in the stream-chat package
// ...so they are forked from their codebase directly

import type { Attachment, FileUploadConfig } from 'stream-chat';

export type LocalAttachment = AnyLocalAttachment | LocalUploadAttachment;

export type LocalUploadAttachment =
  | LocalFileAttachment
  | LocalImageAttachment
  | LocalAudioAttachment
  | LocalVideoAttachment
  | LocalVoiceRecordingAttachment;

export type LocalVoiceRecordingAttachment<CustomLocalMetadata = Record<string, unknown>> =
  LocalAttachmentCast<
    VoiceRecordingAttachment,
    LocalAttachmentUploadMetadata & CustomLocalMetadata
  >;

export type LocalAudioAttachment<CustomLocalMetadata = Record<string, unknown>> =
  LocalAttachmentCast<AudioAttachment, LocalAttachmentUploadMetadata & CustomLocalMetadata>;

export type LocalVideoAttachment<CustomLocalMetadata = Record<string, unknown>> =
  LocalAttachmentCast<VideoAttachment, LocalAttachmentUploadMetadata & CustomLocalMetadata>;

export type LocalImageAttachment<CustomLocalMetadata = Record<string, unknown>> =
  LocalAttachmentCast<ImageAttachment, LocalImageAttachmentUploadMetadata & CustomLocalMetadata>;

export type LocalFileAttachment<CustomLocalMetadata = Record<string, unknown>> =
  LocalAttachmentCast<FileAttachment, LocalAttachmentUploadMetadata & CustomLocalMetadata>;

export type AnyLocalAttachment<CustomLocalMetadata = Record<string, unknown>> = LocalAttachmentCast<
  Attachment,
  LocalAttachmentMetadata<CustomLocalMetadata>
>;

export type LocalAttachmentCast<A, L = Record<string, unknown>> = A & {
  localMetadata: L & BaseLocalAttachmentMetadata;
};

export type LocalAttachmentMetadata<CustomLocalMetadata = Record<string, unknown>> =
  CustomLocalMetadata & BaseLocalAttachmentMetadata & LocalImageAttachmentUploadMetadata;

export type UploadedAttachment =
  | AudioAttachment
  | FileAttachment
  | ImageAttachment
  | VideoAttachment
  | VoiceRecordingAttachment;

export type VoiceRecordingAttachment = Attachment & {
  asset_url: string;
  type: 'voiceRecording';
  duration?: number;
  file_size?: number;
  mime_type?: string;
  title?: string;
  waveform_data?: Array<number>;
};

export type FileAttachment = Attachment & {
  type: 'file';
  asset_url?: string;
  file_size?: number;
  mime_type?: string;
  title?: string;
};

export type AudioAttachment = Attachment & {
  type: 'audio';
  asset_url?: string;
  file_size?: number;
  mime_type?: string;
  title?: string;
};

export type VideoAttachment = Attachment & {
  type: 'video';
  asset_url?: string;
  file_size?: number;
  mime_type?: string;
  thumb_url?: string;
  title?: string;
};

export type ImageAttachment = Attachment & {
  type: 'image';
  fallback?: string;
  image_url?: string;
  original_height?: number;
  original_width?: number;
};

export type BaseLocalAttachmentMetadata = {
  id: string;
};

export type LocalAttachmentUploadMetadata = {
  file: File | FileReference;
  uploadState: AttachmentLoadingState;
  uploadPermissionCheck?: UploadPermissionCheckResult; // added new
};

export type LocalImageAttachmentUploadMetadata = LocalAttachmentUploadMetadata & {
  previewUri?: string;
};

export type AttachmentLoadingState = 'uploading' | 'finished' | 'failed' | 'blocked' | 'pending';

export type UploadPermissionCheckResult = {
  uploadBlocked: boolean;
  reason?: keyof FileUploadConfig;
};

export type FileLike = File | Blob;

// todo: make sure that RN SDK passes MIME type in the type field
export type FileReference = Pick<File, 'name' | 'size' | 'type'> & {
  uri: string;
  // For images
  height?: number;
  width?: number;

  // For voice recordings
  duration?: number;
  waveform_data?: number[];

  // This is specially needed for video in camera roll
  thumb_url?: string;
};

export const isScrapedContent = (attachment: Attachment) =>
  !!attachment?.og_scrape_url || !!attachment?.title_link;

export const isLocalAttachment = (attachment: unknown): attachment is LocalAttachment =>
  !!(attachment as LocalAttachment)?.localMetadata?.id;

export const isLocalUploadAttachment = (attachment: unknown): attachment is LocalUploadAttachment =>
  !!(attachment as LocalAttachment)?.localMetadata?.uploadState;

export const isFileAttachment = (
  attachment: Attachment | LocalAttachment,
  supportedVideoFormat: string[] = []
): attachment is FileAttachment =>
  attachment.type === 'file' ||
  !!(
    attachment.mime_type &&
    supportedVideoFormat.indexOf(attachment.mime_type) === -1 &&
    attachment.type !== 'video'
  );

export const isLocalFileAttachment = (
  attachment: Attachment | LocalAttachment
): attachment is LocalFileAttachment =>
  isFileAttachment(attachment) && isLocalAttachment(attachment);

export const isImageAttachment = (attachment: Attachment): attachment is ImageAttachment =>
  attachment.type === 'image' && !isScrapedContent(attachment);

export const isLocalImageAttachment = (
  attachment: Attachment | LocalAttachment
): attachment is LocalImageAttachment =>
  isImageAttachment(attachment) && isLocalAttachment(attachment);

export const isAudioAttachment = (
  attachment: Attachment | LocalAttachment
): attachment is AudioAttachment => attachment.type === 'audio';

export const isLocalAudioAttachment = (
  attachment: Attachment | LocalAttachment
): attachment is LocalAudioAttachment =>
  isAudioAttachment(attachment) && isLocalAttachment(attachment);

export const isVoiceRecordingAttachment = (
  attachment: Attachment | LocalAttachment
): attachment is VoiceRecordingAttachment => attachment.type === 'voiceRecording';

export const isLocalVoiceRecordingAttachment = (
  attachment: Attachment | LocalAttachment
): attachment is LocalVoiceRecordingAttachment =>
  isVoiceRecordingAttachment(attachment) && isLocalAttachment(attachment);

export const isVideoAttachment = (
  attachment: Attachment | LocalAttachment,
  supportedVideoFormat: string[] = []
): attachment is VideoAttachment =>
  attachment.type === 'video' ||
  !!(attachment.mime_type && supportedVideoFormat.indexOf(attachment.mime_type) !== -1);

export const isLocalVideoAttachment = (
  attachment: Attachment | LocalAttachment
): attachment is LocalVideoAttachment =>
  isVideoAttachment(attachment) && isLocalAttachment(attachment);

export const isUploadedAttachment = (attachment: Attachment): attachment is UploadedAttachment =>
  isAudioAttachment(attachment) ||
  isFileAttachment(attachment) ||
  isImageAttachment(attachment) ||
  isVideoAttachment(attachment) ||
  isVoiceRecordingAttachment(attachment);
